import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { useNotify } from "../../components/notifications";
import { AppDialog } from "../../components/AppDialog";
import { AiCombo } from "../../components/AiCombo";
import { CountryDialog } from "./CountryDialog";
import { ClientDialog } from "./ClientDialog";

interface Option {
  id: string;
  name: string;
}

interface Props {
  /** POST endpoint that creates the standalone contact (/api/v1/clients/contacts). */
  createUrl: string;
  /** The parent Primary Contact select's optionsUrl — re-read after create to select the new row. */
  optionsUrl: string;
  onClose: () => void;
  onAdded: (options: Option[], selectedId: string) => void;
}

type Cell =
  | { kind: "text" | "date"; key: string; label: string; required?: boolean }
  | { kind: "select"; key: string; label: string; src?: string; options?: Option[] }
  | { kind: "ro"; key: string; label: string }
  | { kind: "roNA"; label: string }
  | { kind: "radio" }
  | { kind: "check"; key: FlagKey; label: string }
  | { kind: "photo" };

type FlagKey = "prospect" | "sampleReceiptNotification" | "reportDelivery" | "invoiceDelivery";

const GENDER: Option[] = [{ id: "0", name: "None" }, { id: "1", name: "Male" }, { id: "2", name: "Female" }];

// The legacy Contact DetailView is TWO independent columns (image 861), not a row-major grid: the
// Report/Invoice radio sits under First Name, the delivery flags stack in the left column, and
// Photo + Comment live in the right column. Field key = the ClientContactRequest payload key.
const LEFT: Cell[] = [
  { kind: "ro", key: "fullName", label: "Full Name" },
  { kind: "text", key: "firstName", label: "First Name", required: true },
  { kind: "radio" },
  { kind: "text", key: "middleName", label: "Middle Name" },
  { kind: "text", key: "lastName", label: "Last Name" },
  { kind: "select", key: "gender", label: "Gender", options: GENDER },
  { kind: "date", key: "birthday", label: "Birthday" },
  { kind: "roNA", label: "Client" },
  { kind: "check", key: "prospect", label: "Prospect" },
  { kind: "text", key: "position", label: "Position" },
  { kind: "text", key: "account", label: "Account" },
  { kind: "text", key: "email", label: "Email" },
  { kind: "text", key: "emailCc", label: "Email CC" },
  { kind: "text", key: "emailBcc", label: "Email BCC" },
  { kind: "text", key: "officePhone", label: "Office Phone" },
  { kind: "text", key: "homePhone", label: "Home Phone" },
  { kind: "check", key: "sampleReceiptNotification", label: "Sample Receipt Notification" },
  { kind: "check", key: "reportDelivery", label: "Report Delivery" },
  { kind: "check", key: "invoiceDelivery", label: "Invoice Delivery" },
];
const RIGHT: Cell[] = [
  { kind: "text", key: "mobilePhone", label: "Mobile Phone" },
  { kind: "text", key: "otherPhone", label: "Other Phone" },
  { kind: "text", key: "fax", label: "Fax" },
  { kind: "text", key: "street1", label: "Street 1" },
  { kind: "text", key: "street2", label: "Street 2" },
  { kind: "select", key: "countryId", label: "Country", src: "/api/v1/customers/countries" },
  { kind: "select", key: "stateId", label: "State", src: "/api/v1/customers/states" },
  { kind: "select", key: "cityId", label: "City", src: "/api/v1/customers/cities" },
  { kind: "text", key: "zip", label: "Zip" },
  { kind: "date", key: "retiredDate", label: "Retired Date" },
  { kind: "photo" },
  { kind: "text", key: "comment", label: "Comment" },
];

const SELECT_SRCS = Array.from(
  new Set(
    [...LEFT, ...RIGHT]
      .filter((c): c is Extract<Cell, { kind: "select" }> => c.kind === "select" && !!c.src)
      .map((c) => c.src!),
  ),
);
const DATA_CELLS = [...LEFT, ...RIGHT].filter(
  (c): c is Extract<Cell, { kind: "text" | "date" | "select" }> =>
    c.kind === "text" || c.kind === "date" || c.kind === "select",
);

/** Strip the `data:<mime>;base64,` prefix — the API expects raw base64 for the Photo. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

/**
 * The legacy CRM "Contact" master popup — the Open Prospect ▸ Primary Contact "+" (owner request
 * 2026-10-23, image 861). The full Contact field set laid out as the legacy TWO-column form. First
 * Name is the only required field (legacy Contact.cs). On OK it creates a STANDALONE dbo.Contact
 * (Customer NULL) via POST /api/v1/clients/contacts, then re-reads the Primary Contact options.
 */
export function ContactDialog({ createUrl, optionsUrl, onClose, onAdded }: Props) {
  const notify = useNotify();
  const [v, setV] = useState<Record<string, string>>({ gender: "0" });
  const [type, setType] = useState("0"); // 0 = Report Contact, 1 = Invoice Contact
  const [flags, setFlags] = useState<Record<FlagKey, boolean>>({
    prospect: false,
    sampleReceiptNotification: false,
    reportDelivery: false,
    invoiceDelivery: false,
  });
  const [photo, setPhoto] = useState<File | null>(null);
  const [opts, setOpts] = useState<Record<string, Option[]>>({});
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  // Client "+" and Country "+" (legacy Contact popup's lookup "New" — owner report 2026-10-28,
  // images 1026/1027). A chosen/created Client is the contact's owning Customer (saved UNDER it);
  // empty keeps the contact standalone. Country reuses the shared CountryDialog, Client the ClientDialog.
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientOpen, setClientOpen] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);

  const set = (k: string, val: string) => setV((p) => ({ ...p, [k]: val }));
  const fullName = useMemo(() => `${v.firstName ?? ""} ${v.lastName ?? ""}`.trim(), [v.firstName, v.lastName]);

  useEffect(() => {
    let alive = true;
    void Promise.all(
      SELECT_SRCS.map(async (u) => {
        try {
          const { data } = await api.get<{ data?: Option[] }>(u, { silentLoading: true } as never);
          return [u, data?.data ?? []] as const;
        } catch {
          return [u, [] as Option[]] as const;
        }
      }),
    ).then((pairs) => {
      if (alive) setOpts(Object.fromEntries(pairs));
    });
    return () => {
      alive = false;
    };
  }, []);

  const submit = async () => {
    setTried(true);
    if (!v.firstName?.trim()) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = { type: Number(type), ...flags };
      for (const c of DATA_CELLS) {
        const val = (v[c.key] ?? "").trim();
        if (val) payload[c.key] = val;
      }
      if (photo) payload.photo = await fileToBase64(photo);
      // A chosen/created Client creates the contact UNDER it (POST /clients/{id}/contacts, Customer =
      // the client); with none it stays standalone (the createUrl the caller passed, Customer NULL).
      const postUrl = clientId ? `/api/v1/clients/${clientId}/contacts` : createUrl;
      const created = await api.post<{ data?: { oid?: string } }>(postUrl, payload);
      const newId = created.data?.data?.oid ?? "";
      const { data } = await api.get<{ data?: Option[] }>(optionsUrl, { silentLoading: true } as never);
      const fresh = data?.data ?? [];
      const selected =
        (newId && fresh.find((o) => o.id.toLowerCase() === newId.toLowerCase())?.id) ||
        fresh.find((o) => o.name === fullName)?.id ||
        newId;
      notify.success("Saved successfully.");
      onAdded(fresh, selected);
      onClose();
    } catch {
      /* the shared axios error dialog already surfaced it */
    } finally {
      setSaving(false);
    }
  };

  const cell = (c: Cell, i: number) => {
    if (c.kind === "radio") {
      return (
        <div className="ct-radio" key={`radio-${i}`}>
          <label>
            <input type="radio" name="ct-type" checked={type === "0"} onChange={() => setType("0")} /> Report Contact
          </label>
          <label>
            <input type="radio" name="ct-type" checked={type === "1"} onChange={() => setType("1")} /> Invoice Contact
          </label>
        </div>
      );
    }
    if (c.kind === "check") {
      return (
        <div className="ct-check" key={c.key}>
          <label>
            <input
              type="checkbox"
              checked={flags[c.key]}
              onChange={(e) => setFlags((p) => ({ ...p, [c.key]: e.target.checked }))}
            />{" "}
            {c.label}
          </label>
        </div>
      );
    }
    if (c.kind === "photo") {
      return (
        <div className="op-field" key="photo">
          <label className="op-label" htmlFor="ct-photo">
            Photo:
          </label>
          <input
            id="ct-photo"
            className="op-input op-file"
            type="file"
            accept="image/*"
            onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
          />
        </div>
      );
    }
    const id = `ct-${c.kind === "roNA" ? "client" : c.key}`;
    return (
      <div className="op-field" key={id}>
        <label className="op-label" htmlFor={id}>
          {c.label}
          {c.kind === "text" && c.required ? <> :<span className="req">*</span></> : ":"}
        </label>
        {c.kind === "ro" ? (
          <input id={id} className="op-input" value={fullName} readOnly disabled tabIndex={-1} />
        ) : c.kind === "roNA" ? (
          // Client — the welded AiCombo "+" (one control: value · green ＋ · caret, owner report
          // 2026-10-28 image 1029) opens the full Customer form (ClientDialog); the created client
          // shows here and the contact is saved UNDER it (legacy Contact popup's Client lookup).
          <AiCombo
            value={clientId || null}
            options={clientId ? [{ value: clientId, label: clientName || "Client" }] : []}
            onChange={(val) => { if (!val) { setClientId(""); setClientName(""); } }}
            onNew={() => setClientOpen(true)}
            newTitle="New Client"
            placeholder="N/A"
            naLabel="N/A"
            ariaLabel={c.label}
            compact
          />
        ) : c.kind === "select" && c.key === "countryId" ? (
          // Country — the welded AiCombo "+" opens the shared CountryDialog (legacy Country lookup).
          <AiCombo
            value={v.countryId || null}
            options={(c.options ?? opts[c.src ?? ""] ?? []).map((o) => ({ value: o.id, label: o.name }))}
            onChange={(val) => set("countryId", val ?? "")}
            onNew={() => setCountryOpen(true)}
            newTitle="New Country"
            placeholder="— None —"
            naLabel="— None —"
            ariaLabel={c.label}
            compact
          />
        ) : c.kind === "select" ? (
          <select id={id} className="op-input" value={v[c.key] ?? ""} onChange={(e) => set(c.key, e.target.value)}>
            <option value="">— None —</option>
            {(c.options ?? opts[c.src ?? ""] ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        ) : (
          <input
            id={id}
            className={`op-input${tried && c.kind === "text" && c.required && !v[c.key]?.trim() ? " required-empty" : ""}`}
            type={c.kind === "date" ? "date" : "text"}
            value={v[c.key] ?? ""}
            onChange={(e) => set(c.key, e.target.value)}
          />
        )}
      </div>
    );
  };

  return (
    <AppDialog
      open
      title="Contact"
      icon="ti-user"
      width={880}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={submit} disabled={saving}>
            OK
          </button>
        </>
      }
    >
      <div className="ct-cols">
        <div className="ct-col">{LEFT.map((c, i) => cell(c, i))}</div>
        <div className="ct-col">{RIGHT.map((c, i) => cell(c, i))}</div>
      </div>
      {/* Country "+" — create a new country, add it to the options and select it. */}
      {countryOpen && (
        <CountryDialog
          createUrl="/api/v1/system/countries"
          optionsUrl="/api/v1/customers/countries"
          onClose={() => setCountryOpen(false)}
          onAdded={(fresh, id) => {
            setOpts((o) => ({ ...o, "/api/v1/customers/countries": fresh }));
            if (id) set("countryId", id);
            setCountryOpen(false);
          }}
        />
      )}
      {/* Client "+" — create a new Customer; the contact is then saved UNDER it. The created client's
          display name comes from GET /api/v1/customers/{id} (CustomerDetail.Client). */}
      {clientOpen && (
        <ClientDialog
          createUrl="/api/v1/customers"
          optionsUrl=""
          onClose={() => setClientOpen(false)}
          onAdded={async (_fresh, id) => {
            setClientOpen(false);
            if (!id) return;
            setClientId(id);
            setClientName("Client");
            try {
              const { data } = await api.get<{ data?: { client?: string } }>(
                `/api/v1/customers/${id}`, { silentLoading: true } as never);
              if (data?.data?.client) setClientName(data.data.client);
            } catch { /* keep the fallback label */ }
          }}
        />
      )}
    </AppDialog>
  );
}
